<script setup lang="ts" name="TimelineChartRows">
import { getStickyActionsWidth } from '~/features/useDataTableStickyColumn';

import DataTableActions from '~/components/DataTableActions.vue';
import TimelineChartBarCollapsed from '~/components/TimelineChartBarCollapsed.vue';
import TimelineChartBarExpanded from '~/components/TimelineChartBarExpanded.vue';
import TimelineChartOverlayLines from '~/components/TimelineChartOverlayLines.vue';
import TimelineChartRow from '~/components/TimelineChartRow.vue';
import TimelineChartRowShader from '~/components/TimelineChartRowShader.vue';
import TransitionExpand from '~/components/TransitionExpand.vue';

import type { IDataTableSettingsActions, ITimelineChartRow } from '~/types';

const { actions } = defineProps<{
  totalColumns: number;
  timelineRows: ITimelineChartRow[];
  columnWidth: number;
  scrollWidth: string;
  hoveredRowId: number | string | null;
  todayColumnIndex: number | null;
  actions?: IDataTableSettingsActions;
}>();

const emit = defineEmits<{
  mouseEnter: [string | number];
  mouseLeave: [];
}>();

const actionsWidth = actions && getStickyActionsWidth(actions);
</script>

<template>
  <div
    class="relative"
    :style="{
      width: scrollWidth,
    }"
  >
    <template v-for="row in timelineRows" :key="row.id">
      <TimelineChartRow
        v-if="!row.isOpen"
        :isHovered="hoveredRowId === row.id"
        @mouseenter="emit('mouseEnter', row.id)"
        @mouseleave="emit('mouseLeave')"
      >
        <TimelineChartRowShader v-bind="row.shader" />

        <TimelineChartBarCollapsed
          v-for="bar in row.bars"
          :key="bar.leftOffset"
          :isFaded="timelineRows.some((row) => row.isOpen)"
          v-bind="bar"
        />
      </TimelineChartRow>

      <TimelineChartRow v-if="row.isOpen" isActive />
      <TransitionExpand>
        <div v-if="row.isOpen">
          <TimelineChartRow
            v-for="(bar, index) in row.bars"
            :key="bar.id"
            :isHovered="hoveredRowId === `${row.id}-${bar.id}`"
            :isLastActive="index === row.bars.length - 1"
            size="sm"
            @mouseenter="emit('mouseEnter', `${row.id}-${bar.id}`)"
            @mouseleave="emit('mouseLeave')"
          >
            <TimelineChartBarExpanded
              v-for="subBar in bar.subBars"
              :key="subBar.id"
              v-bind="subBar"
            />
            <template #actions>
              <div
                v-if="actions"
                class="sticky right-0 z-30 ml-auto h-full border-l border-charcoal-2 bg-charcoal-0"
                :style="{ width: `${actionsWidth}px` }"
              >
                <DataTableActions
                  :actions="actions({ rowId: row.id, barId: bar.id })"
                />
              </div>
            </template>
          </TimelineChartRow>
        </div>
      </TransitionExpand>
    </template>

    <TimelineChartOverlayLines
      :totalColumns="totalColumns"
      :todayColumnIndex="todayColumnIndex"
      :columnWidth="columnWidth"
    />
  </div>
</template>
